import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance from '../utils/axios-auth';
const initialState = {
    nodes: [],
    currentNode: null,
    templates: [],
    analytics: null,
    isLoading: false,
    error: null
};
// Async thunks
export const fetchLibraryTree = createAsyncThunk('resourceLibrary/fetchTree', async (_, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.get('/library');
        return response.data.data;
    }
    catch (error) {
        return rejectWithValue(error.response?.data?.message || 'Failed to fetch library tree');
    }
});
export const createNode = createAsyncThunk('resourceLibrary/createNode', async (nodeData, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.post('/nodes', nodeData);
        return response.data.data;
    }
    catch (error) {
        return rejectWithValue(error.response?.data?.message || 'Failed to create node');
    }
});
export const updateNode = createAsyncThunk('resourceLibrary/updateNode', async ({ id, data }, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.put(`/nodes/${id}`, data);
        return response.data.data;
    }
    catch (error) {
        return rejectWithValue(error.response?.data?.message || 'Failed to update node');
    }
});
export const deleteNode = createAsyncThunk('resourceLibrary/deleteNode', async (id, { rejectWithValue }) => {
    try {
        await axiosInstance.delete(`/nodes/${id}`);
        return id;
    }
    catch (error) {
        return rejectWithValue(error.response?.data?.message || 'Failed to delete node');
    }
});
export const fetchLessonContent = createAsyncThunk('resourceLibrary/fetchLessonContent', async (nodeId, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.get(`/lessons/${nodeId}/content`);
        return response.data.data;
    }
    catch (error) {
        return rejectWithValue(error.response?.data?.message || 'Failed to fetch lesson content');
    }
});
export const updateLessonContent = createAsyncThunk('resourceLibrary/updateLessonContent', async ({ nodeId, content, commitMessage }, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.put(`/lessons/${nodeId}/content`, {
            content,
            commit_message: commitMessage
        });
        return response.data.data;
    }
    catch (error) {
        return rejectWithValue(error.response?.data?.message || 'Failed to update lesson content');
    }
});
export const fetchLessonHistory = createAsyncThunk('resourceLibrary/fetchLessonHistory', async (nodeId, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.get(`/lessons/${nodeId}/history`);
        return response.data.data;
    }
    catch (error) {
        return rejectWithValue(error.response?.data?.message || 'Failed to fetch lesson history');
    }
});
export const fetchLessonAnalytics = createAsyncThunk('resourceLibrary/fetchLessonAnalytics', async ({ nodeId, days }, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.get(`/lessons/${nodeId}/analytics?days=${days || 30}`);
        return response.data.data;
    }
    catch (error) {
        return rejectWithValue(error.response?.data?.message || 'Failed to fetch lesson analytics');
    }
});
export const fetchLessonTemplates = createAsyncThunk('resourceLibrary/fetchLessonTemplates', async (filters, { rejectWithValue }) => {
    try {
        const params = new URLSearchParams();
        if (filters?.category)
            params.append('category', filters.category);
        if (filters?.difficulty)
            params.append('difficulty', filters.difficulty);
        const response = await axiosInstance.get(`/templates?${params.toString()}`);
        return response.data.data;
    }
    catch (error) {
        return rejectWithValue(error.response?.data?.message || 'Failed to fetch lesson templates');
    }
});
export const createLessonTemplate = createAsyncThunk('resourceLibrary/createLessonTemplate', async (templateData, { rejectWithValue }) => {
    try {
        const response = await axiosInstance.post('/templates', templateData);
        return response.data.data;
    }
    catch (error) {
        return rejectWithValue(error.response?.data?.message || 'Failed to create lesson template');
    }
});
const resourceLibrarySlice = createSlice({
    name: 'resourceLibrary',
    initialState,
    reducers: {
        setCurrentNode: (state, action) => {
            state.currentNode = action.payload;
        },
        clearCurrentNode: (state) => {
            state.currentNode = null;
        },
        clearError: (state) => {
            state.error = null;
        },
        updateNodeInTree: (state, action) => {
            const { id, updates } = action.payload;
            const updateNodeRecursive = (nodes) => {
                return nodes.map(node => {
                    if (node.id === id) {
                        return { ...node, ...updates };
                    }
                    if (node.children) {
                        return { ...node, children: updateNodeRecursive(node.children) };
                    }
                    return node;
                });
            };
            state.nodes = updateNodeRecursive(state.nodes);
        }
    },
    extraReducers: (builder) => {
        // Fetch Library Tree
        builder
            .addCase(fetchLibraryTree.pending, (state) => {
            state.isLoading = true;
            state.error = null;
        })
            .addCase(fetchLibraryTree.fulfilled, (state, action) => {
            state.isLoading = false;
            state.nodes = action.payload;
        })
            .addCase(fetchLibraryTree.rejected, (state, action) => {
            state.isLoading = false;
            state.error = action.payload;
        });
        // Create Node
        builder
            .addCase(createNode.pending, (state) => {
            state.isLoading = true;
            state.error = null;
        })
            .addCase(createNode.fulfilled, (state, action) => {
            state.isLoading = false;
            // Add the new node to the tree
            if (!action.payload.parent_id) {
                state.nodes.push(action.payload);
            }
            else {
                // Find parent and add child
                const addChildToParent = (nodes, parentId, child) => {
                    return nodes.map(node => {
                        if (node.id === parentId) {
                            return {
                                ...node,
                                children: [...(node.children || []), child]
                            };
                        }
                        if (node.children) {
                            return {
                                ...node,
                                children: addChildToParent(node.children, parentId, child)
                            };
                        }
                        return node;
                    });
                };
                state.nodes = addChildToParent(state.nodes, action.payload.parent_id, action.payload);
            }
        })
            .addCase(createNode.rejected, (state, action) => {
            state.isLoading = false;
            state.error = action.payload;
        });
        // Update Node
        builder
            .addCase(updateNode.pending, (state) => {
            state.isLoading = true;
            state.error = null;
        })
            .addCase(updateNode.fulfilled, (state, action) => {
            state.isLoading = false;
            // Update node in tree
            const updateNodeRecursive = (nodes) => {
                return nodes.map(node => {
                    if (node.id === action.payload.id) {
                        return { ...node, ...action.payload };
                    }
                    if (node.children) {
                        return { ...node, children: updateNodeRecursive(node.children) };
                    }
                    return node;
                });
            };
            state.nodes = updateNodeRecursive(state.nodes);
        })
            .addCase(updateNode.rejected, (state, action) => {
            state.isLoading = false;
            state.error = action.payload;
        });
        // Delete Node
        builder
            .addCase(deleteNode.pending, (state) => {
            state.isLoading = true;
            state.error = null;
        })
            .addCase(deleteNode.fulfilled, (state, action) => {
            state.isLoading = false;
            // Remove node from tree
            const removeNodeRecursive = (nodes) => {
                return nodes.filter(node => {
                    if (node.id === action.payload) {
                        return false;
                    }
                    if (node.children) {
                        node.children = removeNodeRecursive(node.children);
                    }
                    return true;
                });
            };
            state.nodes = removeNodeRecursive(state.nodes);
        })
            .addCase(deleteNode.rejected, (state, action) => {
            state.isLoading = false;
            state.error = action.payload;
        });
        // Fetch Lesson Content
        builder
            .addCase(fetchLessonContent.pending, (state) => {
            state.isLoading = true;
            state.error = null;
        })
            .addCase(fetchLessonContent.fulfilled, (state, action) => {
            state.isLoading = false;
            // Update the current node with lesson content
            if (state.currentNode && state.currentNode.id === action.payload.node_id) {
                state.currentNode = { ...state.currentNode, lesson_content: action.payload };
            }
        })
            .addCase(fetchLessonContent.rejected, (state, action) => {
            state.isLoading = false;
            state.error = action.payload;
        });
        // Update Lesson Content
        builder
            .addCase(updateLessonContent.pending, (state) => {
            state.isLoading = true;
            state.error = null;
        })
            .addCase(updateLessonContent.fulfilled, (state, action) => {
            state.isLoading = false;
            // Update the current node with new lesson content
            if (state.currentNode && state.currentNode.id === action.payload.node_id) {
                state.currentNode = { ...state.currentNode, lesson_content: action.payload };
            }
        })
            .addCase(updateLessonContent.rejected, (state, action) => {
            state.isLoading = false;
            state.error = action.payload;
        });
        // Fetch Lesson Analytics
        builder
            .addCase(fetchLessonAnalytics.pending, (state) => {
            state.isLoading = true;
            state.error = null;
        })
            .addCase(fetchLessonAnalytics.fulfilled, (state, action) => {
            state.isLoading = false;
            state.analytics = action.payload;
        })
            .addCase(fetchLessonAnalytics.rejected, (state, action) => {
            state.isLoading = false;
            state.error = action.payload;
        });
        // Fetch Lesson Templates
        builder
            .addCase(fetchLessonTemplates.pending, (state) => {
            state.isLoading = true;
            state.error = null;
        })
            .addCase(fetchLessonTemplates.fulfilled, (state, action) => {
            state.isLoading = false;
            state.templates = action.payload;
        })
            .addCase(fetchLessonTemplates.rejected, (state, action) => {
            state.isLoading = false;
            state.error = action.payload;
        });
        // Create Lesson Template
        builder
            .addCase(createLessonTemplate.pending, (state) => {
            state.isLoading = true;
            state.error = null;
        })
            .addCase(createLessonTemplate.fulfilled, (state, action) => {
            state.isLoading = false;
            state.templates.push(action.payload);
        })
            .addCase(createLessonTemplate.rejected, (state, action) => {
            state.isLoading = false;
            state.error = action.payload;
        });
    }
});
export const { setCurrentNode, clearCurrentNode, clearError, updateNodeInTree } = resourceLibrarySlice.actions;
export default resourceLibrarySlice.reducer;
